import type { TableCellProps } from '@mui/material';

import { ParameterSortingValuesE } from 'declarations/enums/tickets';

/** Map column label (from Column) to sort parameter for table header */
export const COLUMN_LABEL_TO_SORT_PARAM: Record<string, ParameterSortingValuesE> = {
  P: ParameterSortingValuesE.TicketPriority,
  RO: ParameterSortingValuesE.RONumber,
  Line: ParameterSortingValuesE.LineNumber,
  Age: ParameterSortingValuesE.Age,
  'Claim Age': ParameterSortingValuesE.ClaimAge,
  'Closed RO Age': ParameterSortingValuesE.ClosedROAge,
  'Closed RO Date': ParameterSortingValuesE.ClosedRODate,
  Status: ParameterSortingValuesE.Status,
  Assignee: ParameterSortingValuesE.Assignee,
  Category: ParameterSortingValuesE.RequestCategory,
  Subcategory: ParameterSortingValuesE.SubCategory,
  Account: ParameterSortingValuesE.DMSScheduleAccountNumber,
  'Account Number': ParameterSortingValuesE.DMSScheduleAccountNumber,
  'Claim Status': ParameterSortingValuesE.ClaimStatus,
  Amount: ParameterSortingValuesE.Amount,
  Advisor: ParameterSortingValuesE.Advisor,
  VIN: ParameterSortingValuesE.Vin,
  Tech: ParameterSortingValuesE.Tech,
  Attachment: ParameterSortingValuesE.HasAttachment,
  Approved: ParameterSortingValuesE.IsApprovedByDealer,
  'Audit Trail': ParameterSortingValuesE.AuditTrail,
  'Last Comment': ParameterSortingValuesE.LastComment,
  Chirp: ParameterSortingValuesE.HasTickets,
  'Work Type': ParameterSortingValuesE.WorkType,
};

/** Columns that do not support sorting (label only in header) */
export const NON_SORTABLE_COLUMN_LABELS = new Set<string>(['Chirp']);

export const CHECKBOX_COLUMN_WIDTH_PX = 56;
export const VIEWED_INDICATOR_COLUMN_WIDTH_PX = 28;

/** Width of the fixed checkbox + viewed-indicator columns (px) */
export const FIXED_COLS_WIDTH_PX = CHECKBOX_COLUMN_WIDTH_PX + VIEWED_INDICATOR_COLUMN_WIDTH_PX;

/**
 * Proportional width weights. Weights allocate space; sortable columns need enough
 * room for label + sort arrow. Last Comment yields width for columns that overlap.
 */
export const COLUMN_WIDTH_WEIGHTS: Record<string, number> = {
  P: 2,
  RO: 3,
  Line: 2,
  Age: 2,
  'Claim Age': 4,
  'Closed RO Age': 5,
  'Closed RO Date': 5,
  Status: 3,
  Assignee: 4,
  Category: 5,
  Subcategory: 4,
  Account: 3,
  'Account Number': 3,
  'Last Comment': 6,
  'Claim Status': 5,
  Amount: 4,
  Advisor: 4,
  VIN: 4,
  Tech: 3,
  Attachment: 2,
  Approved: 4,
  Chirp: 2,
  'Audit Trail': 5,
  'Work Type': 4,
};

/**
 * Minimum pixel width per data column. Sortable columns need room for label + sort arrow
 * to prevent overlap. Last Comment (non-sortable) kept lower to free space.
 */
export const COLUMN_MIN_WIDTH_PX: Record<string, number> = {
  P: 32,
  RO: 44,
  Line: 56,
  Age: 44,
  'Claim Age': 76,
  'Closed RO Age': 92,
  'Closed RO Date': 108,
  Status: 60,
  Assignee: 72,
  Category: 76,
  Subcategory: 112,
  Account: 84,
  'Account Number': 72,
  'Last Comment': 37,
  'Claim Status': 82,
  Amount: 60,
  Advisor: 64,
  VIN: 64,
  Tech: 48,
  Attachment: 52,
  Approved: 76,
  Chirp: 52,
  'Audit Trail': 64,
  'Work Type': 88,
};

type ColumnLike = { label: string };

/**
 * Compute total weight for visible columns (used for proportional width math).
 */
export function getTotalWeight(visibleColumns: ColumnLike[]): number {
  return visibleColumns.reduce((sum, col) => sum + (COLUMN_WIDTH_WEIGHTS[col.label] ?? 5), 0);
}

/**
 * Minimum table width so headers + sort arrows never overlap.
 * When container is narrower, horizontal scrollbar appears.
 */
export function getMinTableWidthPx(visibleColumns: ColumnLike[]): number {
  const dataColsMin = visibleColumns.reduce(
    (sum, col) => sum + (COLUMN_MIN_WIDTH_PX[col.label] ?? 56),
    0
  );
  return FIXED_COLS_WIDTH_PX + dataColsMin;
}

/**
 * Return CSS width value for a data column (not checkbox/viewed).
 * Used when container width is not yet known (fallback).
 */
export function getDataColumnWidth(label: string, totalWeight: number): string {
  const weight = COLUMN_WIDTH_WEIGHTS[label] ?? 5;
  const pct = (weight / totalWeight) * 100;
  const fixedShare = (weight / totalWeight) * FIXED_COLS_WIDTH_PX;
  return `calc(${pct}% - ${fixedShare}px)`;
}

/**
 * Return pixel width for a data column given the total width available for data columns.
 * Ensures no column is narrower than COLUMN_MIN_WIDTH_PX so headers don't overlap.
 */
export function getDataColumnWidthPx(
  label: string,
  totalWeight: number,
  dataColumnsTotalWidthPx: number
): number {
  const weight = COLUMN_WIDTH_WEIGHTS[label] ?? 5;
  const proportional = Math.round((weight / totalWeight) * dataColumnsTotalWidthPx);
  const minPx = COLUMN_MIN_WIDTH_PX[label] ?? 0;
  return Math.max(minPx, proportional);
}

export interface TicketTableHeadCellConfig {
  label: string;
  id: string;
  alignment?: TableCellProps['align'];
  minWidth?: number;
  width?: number;
}
